import { FC, ReactNode, useContext, useEffect } from 'react';
import { ReactNotifications } from 'react-notifications-component';
import { useLocation, useNavigate } from 'react-router';
import AuthService from 'src/shared/api/auth/AuthService';
import TokenService from 'src/shared/api/token/TokenService';
import { routesEnum } from 'src/shared/const';
import { UserContext } from 'src/shared/store/user';
import { canAccessGroups } from 'src/shared/store/user/permissions';

import 'react-notifications-component/dist/theme.css';

interface MainContainerProps {
  children?: ReactNode;
}

const resolvePostLoginRedirect = (search: string) => {
  const redirect = new URLSearchParams(search).get('redirect');

  if (!redirect || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return null;
  }

  if (redirect === routesEnum.LOGIN || redirect === routesEnum.RESET) {
    return null;
  }

  return redirect;
};

export const MainContainer: FC<MainContainerProps> = ({ children }) => {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const { user, setUser, isUserLoading, setIsUserLoading, darkMode } = useContext(UserContext);
  const isPublicRoute = pathname === routesEnum.LOGIN || pathname === routesEnum.RESET;
  const hasTokens = Boolean(TokenService.getTokens());
  const canRenderProtectedContent = !isUserLoading && Boolean(user) && hasTokens;

  useEffect(() => {
    (async () => {
      const isFastboardCodeLogin =
        pathname === routesEnum.LOGIN && Boolean(new URLSearchParams(search).get('code'));

      if (isFastboardCodeLogin) {
        setIsUserLoading(false);
        return;
      }

      try {
        if (!TokenService.getAccessToken()) {
          const { data } = await AuthService.refresh();
          TokenService.setTokens(data);
        }

        const { data } = await AuthService.getUser();
        setUser(data.user);
      } catch (e) {
        TokenService.removeTokens();
      } finally {
        setIsUserLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (isUserLoading) {
      return;
    }

    if (!user || !hasTokens) {
      if (isPublicRoute) {
        return;
      }

      return navigate(routesEnum.LOGIN);
    }

    const isTableRoute =
      pathname === routesEnum.TABLE || pathname.startsWith(`${routesEnum.TABLE}/`);
    const isGroupsRoute = pathname === routesEnum.GROUPS;

    if (isGroupsRoute && !canAccessGroups(user)) {
      navigate(routesEnum.TABLE);
      return;
    }

    if (isGroupsRoute || isTableRoute) {
      return;
    }

    if (isPublicRoute) {
      navigate(resolvePostLoginRedirect(search) || routesEnum.TABLE);
      return;
    }

    navigate(routesEnum.TABLE);
  }, [user, hasTokens, isUserLoading, isPublicRoute, navigate, pathname, search]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  return (
    <div className="h-dvh w-dvw bg-white-100 dark:bg-violet-gradient-100">
      <ReactNotifications />

      {isPublicRoute || canRenderProtectedContent ? children : null}
    </div>
  );
};
